import { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { createMassiveEInvoice, previewMassiveEInvoice } from "../../../services/utilsEInvoice";
import "./eInvoiceModals.scss";
import FeedbackAlert from "../../../components/FeedbackAlert/FeedbackAlert";
import "../../../index.scss";
import { formatMoney } from "../../currencyFuntions";
import SalesPointChipsArray from "../SalesPointChipsArray/SalesPointChipsArray";
import DownloadIcon from '@mui/icons-material/Download';

// How many customers the backend is asked to process per request. The server
// also enforces its own time budget and may return fewer; the cursor it hands
// back is authoritative, not this number.
const CHUNK_SIZE = 150;

const MassiveEIModal = ({ onClose }) => {
  const [loadingEInvoice, serLoadingEInvoice] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [resMassiveEI, setResMassiveEI] = useState();

  const [datesToInvoice, setDatesToInvoice] = useState({
    start_date: "",
    end_date: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [excludedSalePoints, setExcludedSalePoints] = useState([]);

  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Set when a chunk failed: holds the cursor to resume from. While this is
  // set the run is paused, not finished.
  const [resumeCursor, setResumeCursor] = useState(null);
  const [halted, setHalted] = useState(false);

  // The exact request body, frozen the moment the user presses the button.
  // The backend re-reads excluded_sale_points on every chunk, so if the form
  // stayed live and someone added a chip mid-run, the early chunks would be
  // billed under one exclusion policy and the rest under another.
  const payloadRef = useRef(null);
  // Accumulated across chunks.
  const resultsRef = useRef({ customers: {}, excel: [], count: 0 });

  const updateExcludedSalePoints = (newSalePoints) => {
    setExcludedSalePoints(newSalePoints);
  };

  const buildPayload = () => ({
    start_date: datesToInvoice.start_date,
    end_date: datesToInvoice.end_date,
    // localStorage returns a STRING, and it must stay one: the backend filters
    // DynamoDB on this value and the attribute is stored as a string there.
    // A numeric 7 matches zero rows silently and every customer comes back
    // "sin transacciones", which looks like a legitimate empty result.
    fk_organization: localStorage.getItem("fk_organization"),
    // Sale point ids stay NUMERIC: the backend compares them against DynamoDB
    // Decimals. A string here does not skip the sale point, it silently
    // disables the exclusion entirely.
    excluded_sale_points: excludedSalePoints.map((salePoint) => salePoint.key),
  });

  // Runs chunk after chunk until the server says there is no more, or until a
  // chunk fails. Never retries automatically.
  const runChunks = async (startCursor) => {
    serLoadingEInvoice(true);
    setErrorMessage("");
    setResumeCursor(null);
    setHalted(false);

    let cursor = startCursor;

    try {
      while (true) {
        const body = { ...payloadRef.current, after_id: cursor, limit: CHUNK_SIZE };
        const res = await createMassiveEInvoice(JSON.stringify(body));

        if (res.error) {
          setErrorMessage(res.error);
          setResumeCursor(cursor);
          return;
        }

        const acc = resultsRef.current;
        acc.customers = { ...acc.customers, ...(res.customers || {}) };
        acc.excel = acc.excel.concat(res.excel || []);
        // Count from the server's own tally. Object.keys() would under-count,
        // because customer_dict is keyed by email and two customers in the same
        // organization can share one.
        acc.count += res.count || 0;

        setProgress({ done: acc.count, total: res.total || 0 });

        if (res.halt) {
          setHalted(true);
          setErrorMessage(
            "El proceso se detuvo: varios clientes seguidos fallaron. " +
            "Normalmente significa que lafactura está rechazando todo " +
            "(por ejemplo, certificado de firma vencido). Revisá el detalle antes de continuar."
          );
          setResMassiveEI(acc.customers);
          return;
        }

        cursor = res.next_after_id;

        if (!res.has_more) {
          setOpenSnackbar(true);
          setResMassiveEI(acc.customers);
          return;
        }
      }
    } catch (err) {
      // Do NOT advance the cursor. If the outcome is unknown the chunk may have
      // issued invoices, and resuming past it would leave them unbilled.
      setResumeCursor(cursor);
      setErrorMessage(
        err.outcomeUnknown
          ? `${err.message} Al continuar, ese bloque se vuelve a procesar: las facturas ya emitidas se omiten automáticamente.`
          : err.message || "Error de conexión durante el proceso."
      );
    } finally {
      serLoadingEInvoice(false);
    }
  };

  const handleSubmitEInvoice = async () => {
    payloadRef.current = buildPayload();
    resultsRef.current = { customers: {}, excel: [], count: 0 };
    setProgress({ done: 0, total: 0 });
    await runChunks(0);
  };

  const handleContinue = async () => {
    await runChunks(resumeCursor);
  };

  const handlePreviewEInvoice = async () => {
    setLoadingPreview(true);

    const jsonObj = buildPayload();

    try {
      const res = await previewMassiveEInvoice(jsonObj);
      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      document.body.appendChild(a);
      a.href = url;
      const dateNow = new Date().toLocaleString("en-US", {
        timeZone: "America/New_York",
      });
      a.download = `EInvoicePreview_${dateNow}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage("Error al descargar el archivo");
    }
    setLoadingPreview(false);
  };

  // Builds the record of what was issued, from the `excel` rows the backend
  // already returns and the UI used to discard. Only rows that actually got a
  // CUFE are invoices; the backend appends a row for every customer examined.
  const handleDownloadCsv = () => {
    const rows = resultsRef.current.excel.filter((row) => row && row.CUFE);
    const header = ["Cliente", "Documento", "Valor", "Documento DIAN", "CUFE"];

    const escape = (value) => {
      const text = value === undefined || value === null ? "" : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const csv = [header.map(escape).join(",")]
      .concat(
        rows.map((row) =>
          [row.name, row.id, row.amount, row.document, row.CUFE].map(escape).join(",")
        )
      )
      .join("\n");

    // BOM so Excel opens the accents correctly.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.style.display = "none";
    document.body.appendChild(a);
    a.href = url;
    a.download = `facturacion_electronica_${datesToInvoice.start_date}_${datesToInvoice.end_date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const validateDates = () => {
    const startDate = new Date(datesToInvoice.start_date);
    const endDate = new Date(datesToInvoice.end_date);

    if (datesToInvoice.start_date === "") {
      setErrorMessage("Porfavor seleccionar la fecha de inicio");
      return false;
    }

    if (datesToInvoice.end_date === "") {
      setErrorMessage("Porfavor seleccionar la fecha de fin");
      return false;
    }

    if (startDate >= endDate) {
      setErrorMessage(
        "La fecha de finalización debe ser mayor que la fecha de inicio"
      );
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const handleInputChangeInner = (event) => {
    const { name, value } = event.target;
    setErrorMessage("");
    setDatesToInvoice((prevDates) => ({
      ...prevDates,
      [name]: value,
    }));
  };

  const statusEIToString = (status) => {
    switch (status) {
      case 422:
        return "No emitida";
      case 200:
        return "Factura emitida";
      case 409:
        return "REVISAR - estado desconocido";
      default:
        return "";
    }
  };

  const summarise = (customers) => {
    const counts = { emitidas: 0, noEmitidas: 0, revisar: 0 };
    Object.keys(customers || {}).forEach((email) => {
      const entry = customers[email];
      if (!entry) return;
      if (entry.status === 200) counts.emitidas += 1;
      else if (entry.status === 409) counts.revisar += 1;
      else counts.noEmitidas += 1;
    });
    return counts;
  };

  // REVISAR first: it is the only outcome that needs a human.
  const sortedEmails = (customers) =>
    Object.keys(customers || {}).sort((a, b) => {
      const rank = (email) => (customers[email] && customers[email].status === 409 ? 0 : 1);
      return rank(a) - rank(b) || a.localeCompare(b);
    });

  const pct =
    progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;

  const formDisabled = loadingEInvoice;

  return (
    <>
      <div className="modal-full-page">
        <div
          className="background-modal-container"
          onClick={loadingEInvoice ? undefined : onClose}
        >
          <div className="background-color-modal-container"></div>
        </div>
        <div className="modal-container-eInvoice">
          {!resMassiveEI && (
            <div>
              <div className="row margin-bottom-1rem">
                <h1 className="section-title">
                  <FormattedMessage id="massiveEInvoiceTitle" />
                </h1>
              </div>
              <div className="row margin-bottom-1rem">
                <p className="info-detail-invoice">
                  Se generarán facturas electrónicas consolidadas para todos los
                  responsables económicos, en el rango de fechas especificado.
                </p>
              </div>

              <form id="createEInvoice" action="" className="">
                <fieldset disabled={formDisabled} style={{ border: "none", padding: 0, margin: 0 }}>
                  <div className="row margin-bottom-1rem">
                    <div className="col-6">
                      <label className="form-label no-padding-6" htmlFor="franchiseInput">
                        <FormattedMessage id="sinceLbl" />
                      </label>
                      <input
                        required
                        type="date"
                        value={datesToInvoice.start_date}
                        name="start_date"
                        onChange={handleInputChangeInner}
                        className="worker-form-select"
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label no-padding-6" htmlFor="franchiseInput">
                        <FormattedMessage id="toLbl" />
                      </label>
                      <input
                        required
                        type="date"
                        value={datesToInvoice.end_date}
                        name="end_date"
                        onChange={handleInputChangeInner}
                        className="worker-form-select"
                      />
                    </div>
                  </div>

                  <div className="row margin-bottom-1rem">
                    <div className="col-12">
                      <label className="form-label no-padding-6" htmlFor="franchiseInput">
                        Excluir puntos de venta
                      </label>
                      <SalesPointChipsArray updateSalePoints={updateExcludedSalePoints} />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-12">
                      <label className="form-label no-padding-6" htmlFor="franchiseInput">
                        Vista previa
                      </label>
                    </div>
                  </div>

                  <div>
                    <button
                      className="btn btn-outline-primary m-2"
                      onClick={(event) => {
                        event.preventDefault();
                        if (validateDates()) {
                          handlePreviewEInvoice();
                        }
                      }}
                    >
                      {!loadingPreview && (
                        <>
                          <DownloadIcon style={{ marginRight: '8px' }} />
                          <FormattedMessage id="downloadLbl" />
                        </>
                      )}
                      {loadingPreview && (
                        <div className="d-flex justify-content-center spinner-container">
                          <div className="spinner-border spinner-secondary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                </fieldset>

                {loadingEInvoice && (
                  <div className="row margin-bottom-1rem">
                    <div className="col-12">
                      <p className="info-detail-invoice">
                        Procesando {progress.done}
                        {progress.total ? ` / ${progress.total}` : ""} clientes… No cierres
                        esta ventana ni vuelvas a presionar el botón.
                      </p>
                      <div className="progress" style={{ height: "18px" }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${pct}%`, backgroundColor: "#01A532" }}
                          aria-valuenow={pct}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        >
                          {pct}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {errorMessage && (
                  <div className="row margin-bottom-1rem">
                    <div className="col-12">
                      <p style={{ color: "#c0392b", margin: 0 }}>{errorMessage}</p>
                    </div>
                  </div>
                )}

                <div className="row mt-2 justify-content-center">
                  <div className="col-12 d-flex justify-content-center">
                    <button
                      className="btn btn-outline-primary m-2"
                      onClick={onClose}
                      disabled={loadingEInvoice}
                    >
                      <FormattedMessage id="cancelLbl" />
                    </button>

                    {resumeCursor !== null && !loadingEInvoice ? (
                      <button
                        className="btn btn-primary report-button"
                        onClick={(event) => {
                          event.preventDefault();
                          handleContinue();
                        }}
                      >
                        CONTINUAR
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary report-button"
                        type="submit"
                        form="createEInvoice"
                        id="first-button-report"
                        disabled={loadingEInvoice}
                        onClick={(event) => {
                          event.preventDefault();
                          if (validateDates()) {
                            handleSubmitEInvoice();
                          }
                        }}
                      >
                        {!loadingEInvoice && (
                          <FormattedMessage id="createMassiveEInvoicelbl" />
                        )}
                        {loadingEInvoice && (
                          <div className="d-flex justify-content-center spinner-container">
                            <div className="spinner-border spinner-secondary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}

          {resMassiveEI && (
            <>
              <div className="row margin-bottom-1rem">
                <h1 className="section-title">
                  Resultado consolidado masivo de facturas
                </h1>
              </div>

              {(() => {
                const counts = summarise(resMassiveEI);
                return (
                  <div className="row margin-bottom-1rem">
                    <div className="col-12">
                      <p className="info-detail-invoice" style={{ margin: 0 }}>
                        <strong>{counts.emitidas}</strong> emitidas ·{" "}
                        <strong>{counts.noEmitidas}</strong> no emitidas
                        {counts.revisar > 0 && (
                          <>
                            {" · "}
                            <strong style={{ color: "#c0392b" }}>
                              {counts.revisar} para revisar
                            </strong>
                          </>
                        )}
                      </p>
                      {halted && (
                        <p style={{ color: "#c0392b" }}>
                          El proceso se detuvo antes de terminar. Las facturas ya emitidas
                          quedaron registradas; al volver a ejecutarlo se omiten
                          automáticamente.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="row margin-bottom-1rem">
                <div className="col-12">
                  <button className="btn btn-outline-primary" onClick={handleDownloadCsv}>
                    <DownloadIcon style={{ marginRight: '8px' }} />
                    Descargar CSV
                  </button>
                </div>
              </div>

              <div className="row resEI-container">
                {sortedEmails(resMassiveEI).map((email, index) => {
                  const entry = resMassiveEI[email] || {};
                  const amount =
                    entry.invoice_body &&
                    entry.invoice_body.invoice &&
                    entry.invoice_body.invoice.amounts
                      ? entry.invoice_body.invoice.amounts.totalAmount
                      : null;
                  const document =
                    entry.response_lafactura &&
                    entry.response_lafactura.invoiceResult &&
                    entry.response_lafactura.invoiceResult.documento
                      ? entry.response_lafactura.invoiceResult.documento.document
                      : null;

                  return (
                    <div className="row ei-sub-container" key={index}>
                      <h6 className="sub-header">{email}</h6>
                      <p style={entry.status === 409 ? { color: "#c0392b" } : undefined}>
                        Estado: {statusEIToString(entry.status)}
                      </p>
                      {entry.text && <p>Mensaje: {entry.text}</p>}
                      {amount !== null && <p>Valor emitido: {formatMoney(amount)}</p>}
                      {document !== null && <p>Documento: {document}</p>}
                    </div>
                  );
                })}
              </div>

              <div className="row justify-content-center">
                <div className="col-12 d-flex justify-content-center">
                  <button className="btn btn-outline-primary m-2" onClick={onClose}>
                    REGRESAR
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <FeedbackAlert
        type={"success"}
        vertical={"bottom"}
        horizontal={"right"}
        idmessage={"eInvoiceSucces"}
        open={openSnackbar}
        setOpen={setOpenSnackbar}
      />
    </>
  );
};

export default MassiveEIModal;
